/**
 * ReadonlyLabeledEdge — the edge used ONLY by the read-only graph preview
 * (PipelineGraphView). It draws the same smoothstep path as the editor's
 * default edges, but takes over label placement so branch chips don't sit on
 * top of the node a "skip" edge routes past.
 *
 * Two things (the "A+B" fix):
 *   A — anchor the label near the edge SOURCE (the fork) instead of the
 *       geometric midpoint. React Flow's built-in label sits at the path
 *       center, which for a long edge that bypasses a node (e.g. fix_intake →
 *       fetch_issue skipping resolve_issue) lands right on that node's title.
 *       Pulling it back toward the source drops it into the free gutter by the
 *       branch point.
 *   B — render the label as an opaque chip raised above node bodies (in the
 *       CSS) so wherever it does land it stays legible.
 *
 * Scoped to readonly: GraphFlowEditor only swaps labeled edges to this type
 * when `readonly` is set, so the editor's interactive "?" conditional edge is
 * untouched.
 */

import React from 'react';
import { EdgeLabelRenderer, BaseEdge, getSmoothStepPath } from 'reactflow';
import './ReadonlyLabeledEdge.css';

export const ReadonlyLabeledEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  label,
}) => {
  // Same path geometry as the editor's default 'smoothstep' edges so the line
  // itself looks identical — only the label moves.
  const [edgePath] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    borderRadius: 8,
  });

  // A — label position: a fixed-ish distance out from the SOURCE along the
  // source→target direction, not the midpoint. ~28% of the run, but at least
  // 30px so short edges still clear the source handle, and capped so a very
  // long edge doesn't drift back toward the middle.
  const dx = targetX - sourceX;
  const dy = targetY - sourceY;
  const len = Math.hypot(dx, dy) || 1;
  const off = Math.min(Math.max(len * 0.28, 30), 64);
  const labelX = sourceX + (dx / len) * off;
  const labelY = sourceY + (dy / len) * off;

  return (
    <>
      <BaseEdge id={id} path={edgePath} markerEnd={markerEnd} style={style} />
      {label && (
        <EdgeLabelRenderer>
          <div
            className="readonly-edge-label"
            style={{
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            }}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

export const ReadonlyLabeledEdgeType = ReadonlyLabeledEdge;
export default ReadonlyLabeledEdge;
